import { CheckCircle2, Lightbulb, AlertTriangle, ChevronDown } from 'lucide-react';
import { slugify } from '../../data/insights';

export const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') ? (
      <strong key={i} className="font-bold text-ink">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );

const isCompactList = (items) => items.length >= 4 && items.every((it) => it.length <= 48 && !it.includes('**'));

const body = 'text-[1.05rem] md:text-lg text-ink/90 leading-relaxed';

const Paragraph = ({ text, dropCap }) => (
  <p
    className={`${body} mb-5 ${
      dropCap ? 'first-letter:font-display first-letter:text-6xl first-letter:font-extrabold first-letter:text-purple first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:mt-1' : ''
    }`}
  >
    {renderInline(text)}
  </p>
);

const List = ({ items, variant }) => {
  if (isCompactList(items)) {
    return (
      <ul className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="article-list-compact">
        {items.map((item, i) => (
          <li key={i} className="border-2 border-ink bg-white px-4 py-3 text-[1rem] font-medium">{renderInline(item)}</li>
        ))}
      </ul>
    );
  }
  if (variant === 1) {
    return (
      <ul className="space-y-3 mb-6" data-testid="article-list-dots">
        {items.map((item, i) => (
          <li key={i} className={`flex items-start gap-4 ${body}`}>
            <span className="mt-3 w-2.5 h-2.5 bg-purple flex-shrink-0" />
            <span>{renderInline(item)}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (variant === 2) {
    return (
      <ul className="space-y-2.5 mb-6 border-l-2 border-ink pl-5" data-testid="article-list-dash">
        {items.map((item, i) => (
          <li key={i} className={body}>{renderInline(item)}</li>
        ))}
      </ul>
    );
  }
  return (
    <ul className="space-y-3 mb-6" data-testid="article-list-check">
      {items.map((item, i) => (
        <li key={i} className={`flex items-start gap-3 ${body}`}>
          <CheckCircle2 className="w-5 h-5 text-purple flex-shrink-0 mt-1" />
          <span>{renderInline(item)}</span>
        </li>
      ))}
    </ul>
  );
};

const Steps = ({ items, variant }) => {
  if (variant === 1) {
    return (
      <ol className="relative ml-3 pl-8 border-l-2 border-ink space-y-6 mb-6" data-testid="article-steps-timeline">
        {items.map((item, i) => (
          <li key={i} className={`relative ${body}`}>
            <span className="absolute -left-[45px] top-0 w-7 h-7 bg-off border-2 border-ink text-ink text-xs font-display font-extrabold flex items-center justify-center">{i + 1}</span>
            {renderInline(item)}
          </li>
        ))}
      </ol>
    );
  }
  if (variant === 2) {
    return (
      <ol className="space-y-5 mb-6" data-testid="article-steps-numerals">
        {items.map((item, i) => (
          <li key={i} className={`flex items-start gap-5 ${body}`}>
            <span className="step-num text-4xl md:text-5xl w-10 flex-shrink-0 tabular-nums">{i + 1}</span>
            <span className="pt-1.5">{renderInline(item)}</span>
          </li>
        ))}
      </ol>
    );
  }
  return (
    <ol className="space-y-4 mb-6" data-testid="article-steps-circles">
      {items.map((item, i) => (
        <li key={i} className={`flex items-start gap-4 ${body}`}>
          <span className="w-8 h-8 bg-purple border-2 border-ink text-off font-display font-extrabold flex items-center justify-center flex-shrink-0 text-sm">{i + 1}</span>
          <span className="pt-0.5">{renderInline(item)}</span>
        </li>
      ))}
    </ol>
  );
};

const Callout = ({ block, variant }) => {
  const isWarn = block.type === 'warn';
  const Icon = isWarn ? AlertTriangle : Lightbulb;
  if (variant === 1) {
    return (
      <aside className={`my-7 border-l-4 pl-5 py-1 ${isWarn ? 'border-amber-500' : 'border-purple'}`} data-testid={`article-${block.type}`}>
        <p className={`label-caps mb-1.5 ${isWarn ? 'text-amber-700' : 'text-purple'}`}>{block.title}</p>
        <p className={`${body} serif-it text-xl`}>{renderInline(block.text)}</p>
      </aside>
    );
  }
  if (variant === 2 && !isWarn) {
    return (
      <div className="my-7 card-ink bg-purple text-off p-6 md:p-7 flex gap-4 items-start" data-testid="article-tip">
        <Icon className="w-6 h-6 text-lilac flex-shrink-0 mt-1" />
        <div>
          <p className="font-display font-bold text-lg mb-1">{block.title}</p>
          <p className="text-[1.05rem] md:text-lg opacity-95 leading-relaxed [&_strong]:text-off">{renderInline(block.text)}</p>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`my-6 border-2 border-ink p-5 md:p-6 ${isWarn ? 'bg-amber-50' : 'bg-lilac/50'}`}
      data-testid={`article-${block.type}`}
    >
      <div className={`flex items-center gap-2 font-display font-bold mb-2 ${isWarn ? 'text-amber-800' : 'text-purple'}`}>
        <Icon className="w-5 h-5" />
        {block.title}
      </div>
      <p className={body}>{renderInline(block.text)}</p>
    </div>
  );
};

const Table = ({ head, rows, variant }) => {
  const wrapper = variant === 0 ? 'overflow-x-auto my-6 border-2 border-ink bg-white' : 'overflow-x-auto my-6';
  const thead = variant === 2 ? 'label-caps text-ink/70' : variant === 1 ? 'border-b-2 border-ink' : 'bg-lilac border-b-2 border-ink';
  const row = variant === 1 ? 'even:bg-white' : 'border-t border-ink/15';
  return (
    <div className={wrapper} data-testid={`article-table-v${variant}`}>
      <table className="w-full text-left text-base md:text-[1.05rem]">
        <thead className={thead}>
          <tr>
            {head.map((h, i) => (
              <th key={i} className={`px-4 py-3 font-display font-bold ${variant === 2 ? '' : 'text-ink'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className={row}>
              {r.map((cell, ci) => (
                <td key={ci} className={`px-4 py-3 align-top text-ink/90 ${variant === 2 && ci === 0 ? 'font-bold text-ink' : ''}`}>{renderInline(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Faq = ({ items }) => (
  <div className="space-y-3 my-6" data-testid="article-faq">
    {items.map((item, i) => (
      <details key={i} className="group border-2 border-ink bg-white open:bg-off open:shadow-ink transition-shadow">
        <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4">
          <h3 className="font-display font-bold text-lg">{item.q}</h3>
          <ChevronDown className="w-5 h-5 text-purple group-open:rotate-180 transition-transform flex-shrink-0" />
        </summary>
        <p className={`px-5 pb-5 ${body}`}>{renderInline(item.a)}</p>
      </details>
    ))}
  </div>
);

const Block = ({ block, variant, dropCap }) => {
  switch (block.type) {
    case 'p':
      return <Paragraph text={block.text} dropCap={dropCap} />;
    case 'list':
      return <List items={block.items} variant={variant} />;
    case 'steps':
      return <Steps items={block.items} variant={variant} />;
    case 'tip':
    case 'warn':
      return <Callout block={block} variant={variant} />;
    case 'table':
      return <Table head={block.head} rows={block.rows} variant={variant} />;
    case 'faq':
      return <Faq items={block.items} />;
    default:
      return null;
  }
};

const h2 = 'font-display font-bold text-2xl md:text-3xl leading-tight';

const Heading = ({ text, index, variant, numbered }) => {
  if (numbered && variant === 0) {
    return (
      <>
        <span className="block label-caps text-purple mb-2">{String(index + 1).padStart(2, '0')}</span>
        <h2 className={`${h2} mb-4`}>{text}</h2>
      </>
    );
  }
  if (numbered && variant === 1) {
    return <h2 className={`${h2} mb-5 border-l-4 border-purple pl-4`}>{text}</h2>;
  }
  if (numbered && variant === 2) {
    return (
      <>
        <h2 className={`${h2} mb-3`}>{text}</h2>
        <div className="w-14 h-1 bg-lilac border border-ink mb-5" />
      </>
    );
  }
  return <h2 className={`${h2} mb-4`}>{text}</h2>;
};

export const ArticleContent = ({ sections, variant = 0, numbered = false, dropCap = false, insertAfter = -1, insertNode = null }) => (
  <div data-testid="article-content" data-variant={variant}>
    {sections.map((s, si) => (
      <div key={s.heading}>
        <section id={slugify(s.heading)} className="mb-10 scroll-mt-28">
          <Heading text={s.heading} index={si} variant={variant} numbered={numbered} />
          {s.blocks.map((b, bi) => (
            <Block key={bi} block={b} variant={variant} dropCap={dropCap && si === 0 && bi === 0 && b.type === 'p'} />
          ))}
        </section>
        {si === insertAfter && insertNode}
      </div>
    ))}
  </div>
);
